import React, { useEffect, useRef } from "react";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, unknown>
          ) => void;
        };
      };
    };
  }
}

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const SCRIPT_ID = "google-gsi-client";

type Props = {
  clientId: string;
  isLight: boolean;
  locale: string;
  onCredential: (credential: string) => void;
  disabled?: boolean;
};

export function GoogleSignInButton({
  clientId,
  isLight,
  locale,
  onCredential,
  disabled,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!clientId || disabled) {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
      return;
    }

    const handleCredential = (response: { credential: string }) => {
      if (response.credential) {
        onCredentialRef.current(response.credential);
      }
    };

    const paint = () => {
      const el = containerRef.current;
      if (!el || !window.google?.accounts?.id) {
        return;
      }
      el.innerHTML = "";
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredential,
      });
      window.google.accounts.id.renderButton(el, {
        type: "standard",
        theme: isLight ? "outline" : "filled_blue",
        size: "large",
        width: 280,
        text: "continue_with",
        locale: locale.replace("_", "-"),
      });
    };

    const existing = document.getElementById(SCRIPT_ID) as
      | HTMLScriptElement
      | null;

    if (window.google?.accounts?.id) {
      paint();
      return;
    }

    if (existing) {
      existing.addEventListener("load", paint, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = GSI_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => paint();
    document.body.appendChild(script);
  }, [clientId, isLight, locale, disabled]);

  if (!clientId) {
    return null;
  }

  return <div ref={containerRef} className="login-v2__google-btn-wrap" />;
}
